import { useOutletContext } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import { CASE_STUDIES } from "@/data/caseStudies";

const SERVICES = [
  "Brand Consulting",
  "Brand Identity",
  "Market Positioning",
  "Brand Architecture",
  "Growth Advisory",
  "Brand Strategy",
  "Visual Systems",
  "Naming",
];

const PROCESS = [
  {
    n: "01",
    title: "Consulting",
    text: "We start in the room, not in Figma. Workshops, customer interviews, and a competitive audit sharp enough to draw blood.",
    duration: "2 weeks",
  },
  {
    n: "02",
    title: "Positioning",
    text: "Tone. Territory. Brief. Signed off before a single pixel is drawn — so the design has a job, not a mood.",
    duration: "2 weeks",
  },
  {
    n: "03",
    title: "Craft",
    text: "Six to ten weeks of quiet, focused identity work. Wordmark, system, assets, and a brand book that reads like a document, not a deck.",
    duration: "6 – 10 weeks",
  },
  {
    n: "04",
    title: "Launch",
    text: "Hand-off with your team, live launch, and six months of follow-through — the tail nobody promises but every founder needs.",
    duration: "6 months",
  },
];

export default function Home() {
  const { onStartProject } = useOutletContext();

  return (
    <div data-testid="home-page">
      <KineticHero onStartProject={onStartProject} />

      <EditorialMarquee />

      {/* Featured work */}
      <section className="py-24 md:py-40 border-b border-charcoal/15" data-testid="section-work">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <SectionHead num="02" label="Selected work" title="Six projects we're not embarrassed to sign." />
          <div className="mt-16 grid grid-cols-12 gap-x-8 gap-y-20 md:gap-y-28">
            {CASE_STUDIES.slice(0, 6).map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
                className={
                  i % 3 === 0
                    ? "col-span-12 md:col-span-7"
                    : i % 3 === 1
                    ? "col-span-12 md:col-span-5 md:mt-32"
                    : "col-span-12 md:col-span-8 md:col-start-3"
                }
              >
                <Link to={`/work/${c.slug}`} className="block group" data-testid={`home-work-${c.slug}`}>
                  <div className={`spotlight frame ${c.aspect} bg-cream`}>
                    <img src={c.cover} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-muted">
                        {c.index} · {c.category}
                      </p>
                      <h3 className="mt-2 font-display text-3xl md:text-4xl tracking-tightest leading-tight">
                        {c.title}
                      </h3>
                    </div>
                    <span className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-muted whitespace-nowrap">
                      {c.year} →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-24 flex justify-end">
            <Link
              to="/work"
              data-testid="home-view-all-work"
              className="inline-flex items-center gap-3 border-b border-charcoal pb-1 text-[12px] uppercase tracking-[0.24em]"
            >
              Index of all work →
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-40 bg-cream border-b border-charcoal/15" data-testid="section-services">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <SectionHead num="03" label="Services" title="Eight things. Done properly." align="left" />
            <p className="mt-8 text-charcoal/70 font-serif italic text-lg leading-relaxed max-w-md">
              We don't run parallel departments. Every engagement is led by a strategist who can draw and a
              designer who can argue.
            </p>
          </div>
          <ul className="col-span-12 md:col-span-7 divide-y divide-charcoal/20">
            {SERVICES.map((s, i) => (
              <li key={s} className="group flex items-baseline gap-6 py-6 md:py-8">
                <span className="font-mono text-[11px] tracking-[0.22em] text-stone-muted w-8">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-3xl md:text-5xl tracking-tightest leading-none text-charcoal group-hover:italic transition-all duration-500">
                  {s}
                </span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more →
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-40 bg-ink text-ivory" data-testid="section-process">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <SectionHead num="04" label="The process" title="Four movements." dark />
          <div className="mt-20 grid grid-cols-12 gap-x-8 gap-y-16 md:gap-y-24">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 }}
                className={i % 2 === 0 ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-6 md:pt-24"}
              >
                <div className="flex items-baseline gap-6 border-t border-ivory/25 pt-6">
                  <span className="numeral text-6xl md:text-7xl text-ivory/40">{p.n}</span>
                  <div className="flex-1">
                    <h3 className="font-display text-4xl md:text-5xl tracking-tightest leading-none">{p.title}</h3>
                    <p className="mt-6 text-ivory/70 leading-relaxed max-w-md">{p.text}</p>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-ivory/50">
                      Duration · {p.duration}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 md:py-40 border-b border-charcoal/15" data-testid="section-stats">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8">
          <Stat n="50+" label="Brands shipped, quietly" col="col-span-12 md:col-span-4" />
          <Stat n="6 – 8" label="Projects a year. On purpose." col="col-span-12 md:col-span-4" />
          <Stat n="2.4×" label="Avg. revenue growth, year one" col="col-span-12 md:col-span-4" />
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 md:py-40 bg-cream" data-testid="section-testimonial">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-stone-muted">[ 06 ] · Testimonial</p>
          <blockquote className="mt-10">
            <p className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tightest text-charcoal">
              "Closing deals got roughly <em className="italic text-stone-muted">40% faster</em> after the rebrand.
              Clients now understand what we do inside thirty seconds — not thirty minutes."
            </p>
            <footer className="mt-12 flex items-center gap-6">
              <div className="h-14 w-14 rounded-full bg-charcoal/10 border border-charcoal/25" />
              <div>
                <p className="font-serif italic">Elena Marchetti</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted mt-1">
                  Founder, Kinetix Eyewear
                </p>
              </div>
            </footer>
          </blockquote>
        </div>
      </section>
    </div>
  );
}

function SectionHead({ num, label, title, dark = false, align = "left" }) {
  return (
    <div className={align === "left" ? "" : "text-right"}>
      <p
        className={`font-mono text-[11px] uppercase tracking-[0.28em] ${
          dark ? "text-ivory/60" : "text-stone-muted"
        }`}
      >
        [ {num} ] · {label}
      </p>
      <h2
        className={`mt-8 font-display text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tightest ${
          dark ? "text-ivory" : "text-charcoal"
        }`}
      >
        {title}
      </h2>
    </div>
  );
}

function Stat({ n, label, col }) {
  return (
    <div className={`${col} border-t border-charcoal/25 pt-8`}>
      <p className="numeral text-7xl md:text-8xl lg:text-[9rem] leading-none tracking-tightest text-charcoal">{n}</p>
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.24em] text-stone-muted">{label}</p>
    </div>
  );
}
